import React, { useEffect, useMemo, useRef, useState } from "react";
import ExcelJS from "exceljs";
import { getLuongXe, saveLuongXe, listPayrollBatches, getPayrollRows, setPayrollRows } from "./happyschoolApi";
import { exportTableExcel } from "./excelExport";

// ===================== Bảng tính mini (giống Excel) =====================
// Workbook: { fileName, sheets: [{ name, maxRow, maxCol, cells: { A1: { v, f, z, b, i, w, h, bg, fc } }, merges: ["A1:B2"], widths: { A: 12 }, hidden: ["H"] }] }
// v = giá trị (số/chuỗi), f = công thức (không có dấu "="), z = định dạng số Excel.

export const colToNum = (c) => { let n = 0; for (const ch of c) n = n * 26 + (ch.charCodeAt(0) - 64); return n; };
export const numToCol = (n) => { let s = ""; while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); } return s; };
const splitAddr = (a) => { const m = /^\$?([A-Z]{1,3})\$?(\d+)$/.exec(a); return m ? { c: colToNum(m[1]), r: Number(m[2]) } : null; };

const ERR = (e) => ({ err: e });
const isErr = (x) => x && typeof x === "object" && "err" in x;
const EXCEL_EPOCH = Date.UTC(1899, 11, 30);
const serialToDate = (n) => new Date(EXCEL_EPOCH + Math.round(n * 86400000));
const dateToSerial = (d) => (Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - EXCEL_EPOCH) / 86400000;
const THU_NGAN = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
const THU_DAI = ["Chủ Nhật", "Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy"];

// ---- Tokenizer ----
function tokenize(src) {
  const t = []; let i = 0;
  const s = src;
  while (i < s.length) {
    const ch = s[i];
    if (ch === " " || ch === "\n" || ch === "\t") { i++; continue; }
    if (ch === '"') { let j = i + 1; let v = ""; while (j < s.length) { if (s[j] === '"') { if (s[j + 1] === '"') { v += '"'; j += 2; continue; } break; } v += s[j]; j++; } t.push({ k: "str", v }); i = j + 1; continue; }
    if (ch === "'") { let j = i + 1; let v = ""; while (j < s.length && s[j] !== "'") { v += s[j]; j++; } i = j + 1; if (s[i] === "!") { i++; t.push({ k: "sheet", v }); } continue; }
    const num = /^(\d+\.?\d*(?:[eE][+-]?\d+)?|\.\d+)/.exec(s.slice(i));
    if (num && !/^[A-Za-z]/.test(s.slice(i + num[0].length, i + num[0].length + 1))) { t.push({ k: "num", v: Number(num[0]) }); i += num[0].length; continue; }
    const id = /^[A-Za-z_À-ỹ][A-Za-z0-9_.À-ỹ]*/.exec(s.slice(i));
    if (ch === "$" || id) {
      const ref = /^\$?[A-Za-z]{1,3}\$?\d+/.exec(s.slice(i));
      if (ref && !/^[A-Za-z0-9_(]/.test(s.slice(i + ref[0].length, i + ref[0].length + 1))) { t.push({ k: "ref", v: ref[0].toUpperCase() }); i += ref[0].length; continue; }
      if (id) {
        if (s[i + id[0].length] === "!") { t.push({ k: "sheet", v: id[0] }); i += id[0].length + 1; continue; }
        t.push({ k: "id", v: id[0].toUpperCase() }); i += id[0].length; continue;
      }
    }
    const two = s.slice(i, i + 2);
    if (two === "<=" || two === ">=" || two === "<>") { t.push({ k: "op", v: two }); i += 2; continue; }
    if ("+-*/^&=<>(),:%;".includes(ch)) { t.push({ k: "op", v: ch === ";" ? "," : ch }); i++; continue; }
    if (ch === "#") { const m = /^#[A-Z0-9/!?]+/.exec(s.slice(i)); t.push({ k: "errlit", v: m ? m[0] : "#" }); i += m ? m[0].length : 1; continue; }
    i++; // ký tự lạ: bỏ qua
  }
  return t;
}

// ---- Parser (đệ quy) -> AST ----
function parse(src) {
  const t = tokenize(src); let p = 0;
  const peek = () => t[p]; const next = () => t[p++];
  const isOp = (v) => t[p] && t[p].k === "op" && t[p].v === v;
  const cmp = () => { let l = cat(); while (t[p] && t[p].k === "op" && ["=", "<>", "<", ">", "<=", ">="].includes(t[p].v)) { const o = next().v; l = { k: "bin", o, l, r: cat() }; } return l; };
  const cat = () => { let l = add(); while (isOp("&")) { next(); l = { k: "bin", o: "&", l, r: add() }; } return l; };
  const add = () => { let l = mul(); while (isOp("+") || isOp("-")) { const o = next().v; l = { k: "bin", o, l, r: mul() }; } return l; };
  const mul = () => { let l = pow(); while (isOp("*") || isOp("/")) { const o = next().v; l = { k: "bin", o, l, r: pow() }; } return l; };
  const pow = () => { let l = una(); while (isOp("^")) { next(); l = { k: "bin", o: "^", l, r: una() }; } return l; };
  const una = () => { if (isOp("-")) { next(); return { k: "neg", e: una() }; } if (isOp("+")) { next(); return una(); } let e = prim(); while (isOp("%")) { next(); e = { k: "bin", o: "/", l: e, r: { k: "num", v: 100 } }; } return e; };
  const refOrRange = (sheet) => {
    const a = next().v;
    if (isOp(":") && t[p + 1] && t[p + 1].k === "ref") { next(); const b = next().v; return { k: "range", sheet, a, b }; }
    return { k: "ref", sheet, a };
  };
  const prim = () => {
    const x = peek();
    if (!x) return { k: "err", v: "#ERROR!" };
    if (x.k === "num") { next(); return { k: "num", v: x.v }; }
    if (x.k === "str") { next(); return { k: "str", v: x.v }; }
    if (x.k === "errlit") { next(); return { k: "err", v: x.v }; }
    if (x.k === "sheet") { next(); if (peek() && peek().k === "ref") return refOrRange(x.v); return { k: "err", v: "#REF!" }; }
    if (x.k === "ref") return refOrRange(null);
    if (x.k === "id") {
      next();
      if (isOp("(")) {
        next(); const args = [];
        if (!isOp(")")) { do { if (isOp(",")) { args.push({ k: "empty" }); continue; } args.push(cmp()); } while (isOp(",") && next()); }
        if (isOp(")")) next();
        return { k: "fn", n: x.v, args };
      }
      if (x.v === "TRUE") return { k: "bool", v: true };
      if (x.v === "FALSE") return { k: "bool", v: false };
      return { k: "err", v: "#NAME?" };
    }
    if (isOp("(")) { next(); const e = cmp(); if (isOp(")")) next(); return e; }
    next(); return { k: "err", v: "#ERROR!" };
  };
  const e = cmp();
  return e;
}

// ---- Định dạng ----
const stripFmt = (z) => String(z || "").replace(/\[[^\]]*\]/g, "").replace(/"[^"]*"/g, "").replace(/\\./g, "");
const isDateFmt = (z) => { const s = stripFmt(z).split(";")[0]; return /[dy]/i.test(s) || (/m/i.test(s) && !/[#0]/.test(s)); };
function fmtDate(n, z) {
  const d = serialToDate(n); const dd = d.getUTCDate(), mm = d.getUTCMonth() + 1, yy = d.getUTCFullYear(), wd = d.getUTCDay();
  const s = stripFmt(z).split(";")[0].trim().toLowerCase();
  if (s === "dddd") return THU_DAI[wd];
  if (s === "ddd") return THU_NGAN[wd];
  if (s === "dd") return String(dd).padStart(2, "0");
  if (s === "d") return String(dd);
  if (/^d+[-/.]m+$/.test(s)) return `${dd}/${mm}`;
  return `${String(dd).padStart(2, "0")}/${String(mm).padStart(2, "0")}/${yy}`;
}
const groupVN = (n, dec = 0) => { const neg = n < 0; const x = Math.abs(n).toFixed(dec); const [a, b] = x.split("."); const g = a.replace(/\B(?=(\d{3})+(?!\d))/g, "."); return (neg ? "-" : "") + g + (b ? "," + b : ""); };
export function formatValue(v, z) {
  if (v === null || v === undefined || v === "") return "";
  if (isErr(v)) return v.err;
  if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
  if (typeof v === "string") return v;
  if (typeof v !== "number" || !isFinite(v)) return String(v);
  const zz = String(z || "General");
  if (zz === "General" || zz === "@" || !zz) { const r = Math.round(v * 1e9) / 1e9; return String(r).replace(".", ","); }
  if (isDateFmt(zz)) return fmtDate(v, zz);
  if (/^0+$/.test(zz)) return String(Math.round(v)).padStart(zz.length, "0");
  const sec = zz.split(";");
  if (v === 0 && sec.length >= 3 && /-/.test(sec[2])) return "-";
  const decM = /0\.(0+)/.exec(sec[0]); const dec = decM ? decM[1].length : 0;
  if (/%/.test(sec[0])) return groupVN(v * 100, dec) + "%";
  if (/#,##/.test(zz)) return groupVN(v, dec);
  return groupVN(v, dec).replace(/\./g, "");
}
// TEXT(v, fmt) của Excel (đủ dùng: ngày/thứ, số).
function excelText(v, f) {
  const z = String(f || "");
  if (typeof v === "number" && isDateFmt(z)) {
    const s = stripFmt(z).trim().toLowerCase();
    const d = serialToDate(v); const wd = d.getUTCDay();
    if (s === "ddd") return THU_NGAN[wd];
    if (s === "dddd") return THU_DAI[wd];
    return s.replace(/yyyy|yy|mm|m|dd|d/g, (k) => {
      if (k === "yyyy") return String(d.getUTCFullYear()); if (k === "yy") return String(d.getUTCFullYear()).slice(2);
      if (k === "mm") return String(d.getUTCMonth() + 1).padStart(2, "0"); if (k === "m") return String(d.getUTCMonth() + 1);
      if (k === "dd") return String(d.getUTCDate()).padStart(2, "0"); return String(d.getUTCDate());
    });
  }
  if (typeof v === "number") return formatValue(v, z);
  return String(v ?? "");
}

// ---- Bộ tính ----
export function makeEngine(sheets) {
  const byName = {}; sheets.forEach((s, i) => { byName[String(s.name).toLowerCase()] = i; });
  const ast = new Map(); const memo = new Map(); const busy = new Set();
  const sheetOf = (si, name) => (name == null ? si : byName[String(name).toLowerCase()]);
  const getAst = (f) => { if (!ast.has(f)) { try { ast.set(f, parse(f)); } catch (e) { ast.set(f, { k: "err", v: "#ERROR!" }); } } return ast.get(f); };
  const cellVal = (si, addr) => {
    const key = si + "!" + addr;
    if (memo.has(key)) return memo.get(key);
    const sh = sheets[si]; const c = sh && sh.cells[addr];
    if (!c) return null;
    if (!c.f) return c.v === undefined ? null : c.v;
    if (busy.has(key)) return ERR("#CIRC!");
    busy.add(key);
    let v; try { v = ev(getAst(c.f), si); if (v && v.range) v = v.vals[0] ?? null; if (v === null) v = 0; } catch (e) { v = ERR("#ERROR!"); }
    busy.delete(key); memo.set(key, v); return v;
  };
  const rangeVals = (si, a, b) => {
    const A = splitAddr(a), B = splitAddr(b); if (!A || !B) return [];
    const out = [];
    const sh = sheets[si];
    const r2 = Math.min(Math.max(A.r, B.r), (sh && sh.maxRow) || 0), c2 = Math.min(Math.max(A.c, B.c), (sh && sh.maxCol) || 0);
    for (let r = Math.min(A.r, B.r); r <= r2; r++) for (let c = Math.min(A.c, B.c); c <= c2; c++) out.push(cellVal(si, numToCol(c) + r));
    return out;
  };
  const num = (x) => { if (isErr(x)) return x; if (x === null || x === "" || x === undefined) return 0; if (typeof x === "boolean") return x ? 1 : 0; if (typeof x === "number") return x; const n = Number(String(x).replace(",", ".")); return isNaN(n) ? ERR("#VALUE!") : n; };
  const str = (x) => (x === null || x === undefined ? "" : typeof x === "number" ? String(Math.round(x * 1e9) / 1e9) : typeof x === "boolean" ? (x ? "TRUE" : "FALSE") : String(x));
  const flat = (args, si) => { const out = []; args.forEach((a) => { const v = ev(a, si); if (v && v.range) out.push(...v.vals.map((x) => ({ x, r: true }))); else out.push({ x: v, r: false }); }); return out; };
  const nums = (args, si) => { const out = []; for (const { x, r } of flat(args, si)) { if (isErr(x)) return x; if (typeof x === "number") out.push(x); else if (!r && x !== null && x !== "") { const n = num(x); if (isErr(n)) return n; out.push(n); } } return out; };
  const agg = (n, list) => {
    if (isErr(list)) return list;
    switch (n) {
      case 1: return list.length ? list.reduce((a, b) => a + b, 0) / list.length : ERR("#DIV/0!");
      case 2: return list.length;
      case 4: return list.length ? Math.max(...list) : 0;
      case 5: return list.length ? Math.min(...list) : 0;
      case 6: return list.reduce((a, b) => a * b, 1);
      default: return list.reduce((a, b) => a + b, 0);
    }
  };
  const round = (x, d, mode) => { const m = Math.pow(10, d || 0); const y = x * m; const r = mode === "up" ? Math.sign(y) * Math.ceil(Math.abs(y) - 1e-9) : mode === "down" ? Math.trunc(y) : Math.sign(y) * Math.round(Math.abs(y) + 1e-9); return r / m; };
  const truthy = (x) => (typeof x === "string" ? x !== "" && x.toUpperCase() !== "FALSE" : !!num(x));
  const FN = {
    SUM: (a, si) => agg(9, nums(a, si)),
    AVERAGE: (a, si) => agg(1, nums(a, si)),
    COUNT: (a, si) => flat(a, si).filter(({ x }) => typeof x === "number").length,
    COUNTA: (a, si) => flat(a, si).filter(({ x }) => x !== null && x !== "").length,
    MAX: (a, si) => agg(4, nums(a, si)),
    MIN: (a, si) => agg(5, nums(a, si)),
    PRODUCT: (a, si) => agg(6, nums(a, si)),
    SUBTOTAL: (a, si) => { const n = num(ev(a[0], si)); if (isErr(n)) return n; const k = n % 100; if (k === 3) return flat(a.slice(1), si).filter(({ x }) => x !== null && x !== "").length; if (k === 2) return flat(a.slice(1), si).filter(({ x }) => typeof x === "number").length; return agg(k, nums(a.slice(1), si)); },
    ROUND: (a, si) => { const x = num(ev(a[0], si)), d = num(ev(a[1], si)); return isErr(x) ? x : isErr(d) ? d : round(x, d); },
    ROUNDUP: (a, si) => { const x = num(ev(a[0], si)), d = num(ev(a[1], si)); return isErr(x) ? x : round(x, d, "up"); },
    ROUNDDOWN: (a, si) => { const x = num(ev(a[0], si)), d = num(ev(a[1], si)); return isErr(x) ? x : round(x, d, "down"); },
    INT: (a, si) => { const x = num(ev(a[0], si)); return isErr(x) ? x : Math.floor(x); },
    ABS: (a, si) => { const x = num(ev(a[0], si)); return isErr(x) ? x : Math.abs(x); },
    IF: (a, si) => { const c = ev(a[0], si); if (isErr(c)) return c; return truthy(c) ? (a[1] ? ev(a[1], si) : true) : (a[2] ? ev(a[2], si) : false); },
    IFERROR: (a, si) => { const v = ev(a[0], si); return isErr(v) ? ev(a[1], si) : v; },
    AND: (a, si) => flat(a, si).every(({ x }) => truthy(x)),
    OR: (a, si) => flat(a, si).some(({ x }) => truthy(x)),
    NOT: (a, si) => !truthy(ev(a[0], si)),
    DATE: (a, si) => { const y = num(ev(a[0], si)), m = num(ev(a[1], si)), d = num(ev(a[2], si)); if ([y, m, d].some(isErr)) return ERR("#VALUE!"); return (Date.UTC(y, m - 1, d) - EXCEL_EPOCH) / 86400000; },
    DAY: (a, si) => serialToDate(num(ev(a[0], si))).getUTCDate(),
    MONTH: (a, si) => serialToDate(num(ev(a[0], si))).getUTCMonth() + 1,
    YEAR: (a, si) => serialToDate(num(ev(a[0], si))).getUTCFullYear(),
    WEEKDAY: (a, si) => serialToDate(num(ev(a[0], si))).getUTCDay() + 1,
    TEXT: (a, si) => excelText(ev(a[0], si), str(ev(a[1], si))),
    CONCATENATE: (a, si) => flat(a, si).map(({ x }) => str(x)).join(""),
    LEN: (a, si) => str(ev(a[0], si)).length,
    VALUE: (a, si) => num(ev(a[0], si)),
    SUMIF: (a, si) => {
      const R = ev(a[0], si), C = ev(a[1], si), S = a[2] ? ev(a[2], si) : R;
      if (!R || !R.range) return ERR("#VALUE!");
      const crit = str(C); const m = /^(<=|>=|<>|<|>|=)?(.*)$/.exec(crit); const op = m[1] || "="; const rhs = m[2]; const rn = Number(rhs);
      const test = (x) => { if (!isNaN(rn) && rhs !== "") { const n = typeof x === "number" ? x : NaN; return op === "=" ? n === rn : op === "<>" ? n !== rn : op === "<" ? n < rn : op === ">" ? n > rn : op === "<=" ? n <= rn : n >= rn; } const s = str(x).toLowerCase(); return op === "<>" ? s !== rhs.toLowerCase() : s === rhs.toLowerCase(); };
      let t = 0; R.vals.forEach((x, i) => { if (test(x)) { const y = S.range ? S.vals[i] : null; if (typeof y === "number") t += y; } }); return t;
    },
  };
  function ev(n, si) {
    switch (n.k) {
      case "num": return n.v;
      case "str": return n.v;
      case "bool": return n.v;
      case "err": return ERR(n.v);
      case "empty": return null;
      case "ref": { const s = sheetOf(si, n.sheet); if (s === undefined) return ERR("#REF!"); return cellVal(s, n.a.replace(/\$/g, "")); }
      case "range": { const s = sheetOf(si, n.sheet); if (s === undefined) return ERR("#REF!"); return { range: true, vals: rangeVals(s, n.a.replace(/\$/g, ""), n.b.replace(/\$/g, "")) }; }
      case "neg": { const x = num(ev(n.e, si)); return isErr(x) ? x : -x; }
      case "fn": { const f = FN[n.n]; return f ? f(n.args, si) : ERR("#NAME?"); }
      case "bin": {
        const L = ev(n.l, si), R = ev(n.r, si);
        const l = L && L.range ? L.vals[0] : L, r = R && R.range ? R.vals[0] : R;
        if (isErr(l)) return l; if (isErr(r)) return r;
        if (n.o === "&") return str(l) + str(r);
        if (["=", "<>", "<", ">", "<=", ">="].includes(n.o)) {
          const bothNum = (typeof l === "number" || l === null) && (typeof r === "number" || r === null);
          const a = bothNum ? (l || 0) : str(l).toLowerCase(), b = bothNum ? (r || 0) : str(r).toLowerCase();
          return n.o === "=" ? a === b : n.o === "<>" ? a !== b : n.o === "<" ? a < b : n.o === ">" ? a > b : n.o === "<=" ? a <= b : a >= b;
        }
        const a = num(l), b = num(r); if (isErr(a)) return a; if (isErr(b)) return b;
        if (n.o === "+") return a + b; if (n.o === "-") return a - b; if (n.o === "*") return a * b;
        if (n.o === "/") return b === 0 ? ERR("#DIV/0!") : a / b;
        if (n.o === "^") return Math.pow(a, b);
        return ERR("#ERROR!");
      }
      default: return ERR("#ERROR!");
    }
  }
  return { value: cellVal, sheetIndex: (name) => byName[String(name).toLowerCase()] };
}

// Dời tham chiếu ô khi chèn/xoá dòng (chỉ tham chiếu trong cùng sheet, không có tên sheet).
function shiftFormula(f, at, n, sameSheetName) {
  // tách theo chuỗi "..." để không sửa bên trong chuỗi
  return f.split(/("(?:[^"]|"")*")/).map((part, i) => {
    if (i % 2 === 1) return part;
    return part.replace(/('[^']*'!|[A-Za-z_][A-Za-z0-9_.]*!)?(\$?)([A-Z]{1,3})(\$?)(\d+)(?![\d(A-Za-z_])/g, (m, sh, d1, col, d2, row) => {
      if (sh && sh.replace(/[!']/g, "").toLowerCase() !== String(sameSheetName).toLowerCase()) return m;
      const r = Number(row);
      if (r < at) return m;
      const nr = r + n;
      if (nr < at) return (sh || "") + "#REF!";
      return `${sh || ""}${d1}${col}${d2}${nr}`;
    });
  }).join("");
}
// Công thức dòng trên -> dòng dưới (tham chiếu tương đối +1).
function relShift(f, dr) {
  return f.split(/("(?:[^"]|"")*")/).map((part, i) => (i % 2 === 1 ? part : part.replace(/('[^']*'!|[A-Za-z_][A-Za-z0-9_.]*!)?(\$?)([A-Z]{1,3})(\$?)(\d+)(?![\d(A-Za-z_])/g, (m, sh, d1, col, d2, row) => `${sh || ""}${d1}${col}${d2}${d2 ? row : Number(row) + dr}`))).join("");
}
function insertRow(wb, si, at, copyFrom) {
  const sh = wb.sheets[si];
  const cells = {};
  Object.entries(sh.cells).forEach(([k, c]) => { const a = splitAddr(k); const nk = a.r >= at ? numToCol(a.c) + (a.r + 1) : k; cells[nk] = { ...c }; });
  if (copyFrom) Object.entries(sh.cells).forEach(([k, c]) => { const a = splitAddr(k); if (a.r === copyFrom) { const o = { ...c }; if (o.f) o.f = relShift(o.f, at - copyFrom); delete o.v; cells[numToCol(a.c) + at] = o; } });
  const sheets = wb.sheets.map((s, i) => {
    const base = i === si ? { ...sh, cells, maxRow: sh.maxRow + 1 } : s;
    const cs = {};
    Object.entries(base.cells).forEach(([k, c]) => { cs[k] = c.f && !(i === si && copyFrom && splitAddr(k).r === at) ? { ...c, f: i === si ? shiftFormula(c.f, at, 1, sh.name) : shiftFormulaOther(c.f, at, 1, sh.name) } : c; });
    return { ...base, cells: cs, merges: i === si ? (base.merges || []).map((m) => m.split(":").map((x) => { const a = splitAddr(x); return a.r >= at ? numToCol(a.c) + (a.r + 1) : x; }).join(":")) : base.merges };
  });
  return { ...wb, sheets };
}
function shiftFormulaOther(f, at, n, name) {
  // ở sheet khác: chỉ dời tham chiếu có tên sheet = name
  return f.split(/("(?:[^"]|"")*")/).map((part, i) => (i % 2 === 1 ? part : part.replace(/('[^']*'!|[A-Za-z_][A-Za-z0-9_.]*!)(\$?)([A-Z]{1,3})(\$?)(\d+)(?![\d(A-Za-z_])/g, (m, sh, d1, col, d2, row) => {
    if (sh.replace(/[!']/g, "").toLowerCase() !== String(name).toLowerCase()) return m;
    const r = Number(row); if (r < at) return m; const nr = r + n; if (nr < at) return sh + "#REF!"; return `${sh}${d1}${col}${d2}${nr}`;
  }))).join("");
}
function deleteRow(wb, si, at) {
  const sh = wb.sheets[si];
  const cells = {};
  Object.entries(sh.cells).forEach(([k, c]) => { const a = splitAddr(k); if (a.r === at) return; const nk = a.r > at ? numToCol(a.c) + (a.r - 1) : k; cells[nk] = c; });
  // Tham chiếu tới dòng bị xoá: nếu là đầu/cuối của vùng thì co vùng, nếu là ô lẻ -> #REF!
  const fix = (f, sameName, isSame) => f.split(/("(?:[^"]|"")*")/).map((part, i) => {
    if (i % 2 === 1) return part;
    return part.replace(/('[^']*'!|[A-Za-z_][A-Za-z0-9_.]*!)?(\$?[A-Z]{1,3}\$?\d+)(?::(\$?[A-Z]{1,3}\$?\d+))?(?![\d(A-Za-z_])/g, (m, shn, a, b) => {
      const mine = shn ? shn.replace(/[!']/g, "").toLowerCase() === String(sameName).toLowerCase() : isSame;
      if (!mine) return m;
      const mv = (x, end) => { const mm = /^(\$?)([A-Z]{1,3})(\$?)(\d+)$/.exec(x); let r = Number(mm[4]); if (r > at || (r === at && end)) r -= 1; return `${mm[1]}${mm[2]}${mm[3]}${r}`; };
      if (b) { const ra = Number(/\d+$/.exec(a)[0]), rb = Number(/\d+$/.exec(b)[0]); if (ra === at && rb === at) return (shn || "") + "#REF!"; return `${shn || ""}${mv(a, false)}:${mv(b, true)}`; }
      if (Number(/\d+$/.exec(a)[0]) === at) return (shn || "") + "#REF!";
      return `${shn || ""}${mv(a, false)}`;
    });
  }).join("");
  const sheets = wb.sheets.map((s, i) => {
    const base = i === si ? { ...sh, cells, maxRow: Math.max(1, sh.maxRow - 1) } : s;
    const cs = {}; Object.entries(base.cells).forEach(([k, c]) => { cs[k] = c.f ? { ...c, f: fix(c.f, sh.name, i === si) } : c; });
    return { ...base, cells: cs, merges: i === si ? (base.merges || []).filter((m) => !m.split(":").every((x) => splitAddr(x).r === at)).map((m) => m.split(":").map((x) => { const a = splitAddr(x); return a.r > at ? numToCol(a.c) + (a.r - 1) : x; }).join(":")) : base.merges };
  });
  return { ...wb, sheets };
}

// ---- Nhập / xuất Excel (exceljs) ----
export async function readWorkbookFile(file) {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const sheets = [];
  wb.eachSheet((ws) => {
    const cells = {}; let maxRow = 0, maxCol = 0;
    ws.eachRow({ includeEmpty: false }, (row, r) => {
      row.eachCell({ includeEmpty: false }, (cell, c) => {
        const o = {}; const val = cell.value;
        let f = null;
        try { f = cell.formula || (val && val.sharedFormula ? cell.formula : null); } catch (e) { f = null; }
        if (f) { o.f = String(f).replace(/^=/, ""); const res = val && val.result; if (typeof res === "number" || typeof res === "string") o.v = res; }
        else if (val instanceof Date) o.v = dateToSerial(new Date(val.getUTCFullYear(), val.getUTCMonth(), val.getUTCDate()));
        else if (val && typeof val === "object" && val.richText) o.v = val.richText.map((x) => x.text).join("");
        else if (val && typeof val === "object" && "text" in val) o.v = String(val.text);
        else if (val && typeof val === "object" && "error" in val) o.v = String(val.error);
        else if (typeof val === "number" || typeof val === "string") o.v = val;
        else if (typeof val === "boolean") o.v = val ? 1 : 0;
        if (cell.numFmt && cell.numFmt !== "General") o.z = cell.numFmt;
        if (cell.font && cell.font.bold) o.b = 1;
        if (cell.font && cell.font.italic) o.i = 1;
        if (cell.font && cell.font.color && /^FF[0-9A-F]{6}$/i.test(cell.font.color.argb || "") && cell.font.color.argb.toUpperCase() !== "FF000000") o.fc = "#" + cell.font.color.argb.slice(2);
        if (cell.alignment && cell.alignment.wrapText) o.w = 1;
        if (cell.alignment && /^(left|center|right)$/.test(cell.alignment.horizontal || "")) o.h = cell.alignment.horizontal;
        const fg = cell.fill && cell.fill.type === "pattern" && cell.fill.fgColor && cell.fill.fgColor.argb;
        if (fg && /^FF[0-9A-F]{6}$/i.test(fg) && fg.toUpperCase() !== "FFFFFFFF") o.bg = "#" + fg.slice(2);
        if (o.v === undefined && !o.f && !o.bg) return;
        cells[numToCol(c) + r] = o; maxRow = Math.max(maxRow, r); maxCol = Math.max(maxCol, c);
      });
    });
    const widths = {}; const hidden = [];
    for (let c = 1; c <= maxCol; c++) { const col = ws.getColumn(c); if (col.width) widths[numToCol(c)] = Math.round(col.width * 10) / 10; if (col.hidden) hidden.push(numToCol(c)); }
    const merges = ((ws.model && ws.model.merges) || []).filter((m) => /^[A-Z]+\d+:[A-Z]+\d+$/.test(m));
    sheets.push({ name: ws.name, maxRow, maxCol, cells, merges, widths, hidden });
  });
  return { fileName: file.name, sheets };
}
async function exportWorkbook(wbData, fileName) {
  const eng = makeEngine(wbData.sheets);
  const wb = new ExcelJS.Workbook();
  const font = { name: "Times New Roman", size: 12 };
  wbData.sheets.forEach((sh, si) => {
    const ws = wb.addWorksheet(String(sh.name).slice(0, 31));
    Object.entries(sh.widths || {}).forEach(([c, w]) => { ws.getColumn(colToNum(c)).width = w; });
    (sh.hidden || []).forEach((c) => { ws.getColumn(colToNum(c)).hidden = true; });
    Object.entries(sh.cells).forEach(([k, c]) => {
      const cell = ws.getCell(k);
      if (c.f) { const r = eng.value(si, k); cell.value = { formula: c.f, result: isErr(r) ? undefined : r === null ? undefined : r }; }
      else if (c.v !== undefined) cell.value = c.v;
      if (c.z) cell.numFmt = c.z;
      cell.font = { ...font, bold: !!c.b, italic: !!c.i, ...(c.fc ? { color: { argb: "FF" + c.fc.slice(1) } } : {}) };
      if (c.bg) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF" + c.bg.slice(1) } };
      cell.alignment = { vertical: "middle", wrapText: !!c.w, ...(c.h ? { horizontal: c.h } : {}) };
      cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
    });
    (sh.merges || []).forEach((m) => { try { ws.mergeCells(m); } catch (e) { /* bỏ qua */ } });
  });
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = fileName; document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ===================== Tab "Lái xe qua lương GV" =====================
// Lấy các dòng có Ghi chú "Đi qua tài khoản cùng lương GV..." ở mọi sheet; số liệu tính trực tiếp từ bảng (đổi bảng là đổi theo).
const MARK = "qua tài khoản cùng lương gv";
export const keyTen = (x) => String(x || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/đ/gi, "d").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 80);
export function laiXeQuaLuongGV(wb, eng) {
  const out = [];
  wb.sheets.forEach((sh, si) => {
    const txt = (addr) => { const c = sh.cells[addr]; return c && typeof c.v === "string" ? c.v.trim() : ""; };
    const col = {};
    for (let r = 1; r <= Math.min(8, sh.maxRow); r++) for (let c = 1; c <= sh.maxCol; c++) {
      const t = txt(numToCol(c) + r).toLowerCase(); if (!t) continue;
      const set = (k) => { if (!col[k]) col[k] = numToCol(c); };
      if (t === "khối") set("khoi"); else if (t === "tuyến") set("tuyen"); else if (t === "tài xế") set("taiXe"); else if (t === "cung đường") set("cungDuong");
      else if (/^cước chốt t\d/.test(t)) set("cuoc"); else if (t === "tổng công") set("cong"); else if (t === "tổng tiền") set("tongTien");
      else if (t.startsWith("vé cầu")) set("ve"); else if (t === "trả lái xe") set("tra"); else if (t === "stk") set("stk");
      else if (t === "ngân hàng") set("nh"); else if (t === "chủ tk") set("chuTk"); else if (t === "ghi chú") set("ghiChu");
    }
    if (!col.tra) return;
    for (let r = 1; r <= sh.maxRow; r++) {
      let note = "";
      for (let c = 1; c <= sh.maxCol; c++) { const t = txt(numToCol(c) + r); if (t && t.toLowerCase().includes(MARK)) { note = t; break; } }
      if (!note) continue;
      const val = (k) => (col[k] ? (sh.cells[col[k] + r] ? (sh.cells[col[k] + r].f ? eng.value(si, col[k] + r) : sh.cells[col[k] + r].v) : null) : null);
      const n = (k) => { const v = val(k); return typeof v === "number" ? v : 0; };
      const s = (k) => { const v = val(k); return v === null || v === undefined || isErr(v) ? "" : String(v); };
      out.push({ sheet: sh.name, row: r, khoi: s("khoi"), tuyen: s("tuyen"), taiXe: s("taiXe").trim(), cungDuong: s("cungDuong").trim(), cuoc: n("cuoc"), cong: n("cong"), tongTien: n("tongTien"), ve: n("ve"), thuNhap: n("tra"), stk: s("stk"), nh: s("nh"), chuTk: s("chuTk").trim(), ghiChu: note, traAddr: col.tra + r });
    }
  });
  return out;
}

// ===================== Tab "Chuyển khoản" =====================
// Mỗi dòng có Số tài khoản (ô số TK, ngay sau là ô Ngân hàng, rồi Chủ TK) ở mọi sheet -> 1 khoản chi.
// Số tiền: sheet có cột "Trả lái xe" thì lấy cột đó; sheet khác lấy ô số tiền gần nhất bên trái ô STK.
// Gộp theo STK + Ngân hàng (1 người nhận nhiều tuyến = 1 lần chuyển). Lái xe "qua lương GV" tách riêng.
const BANK_RE = /bank|vietin|viettin|bidv|agri|techcom|vietcom|\bmb\b|acb|tpbank|sacom|vpbank|shb|hdbank|ocb|seabank|lienviet|lpbank|msb|eximbank|nam ?a|kienlong|bac ?a|scb|abbank|vib|pvcom|bao ?viet|ncb|dong ?a|saigon|oceanbank|gpbank|cbbank|vietbank|baovietbank/i;
export function chuyenKhoanXe(wb, eng, monthLabel) {
  const out = [];
  wb.sheets.forEach((sh, si) => {
    const raw = (addr) => { const c = sh.cells[addr]; if (!c) return null; return c.f ? eng.value(si, addr) : c.v === undefined ? null : c.v; };
    const txt = (addr) => { const v = raw(addr); return v === null || isErr(v) ? "" : String(v).trim(); };
    let traCol = null; let ndCol = null;
    for (let r = 1; r <= Math.min(8, sh.maxRow); r++) for (let c = 1; c <= sh.maxCol; c++) {
      const h = txt(numToCol(c) + r).toLowerCase();
      if (h === "trả lái xe") traCol = traCol || numToCol(c);
      if (h === "nội dung chuyển khoản") ndCol = ndCol || numToCol(c); // cột tuỳ chọn: ghi đè nội dung chuyển khoản của dòng
    }
    let khoi = ""; // tiêu đề khối (vd CANON/ELENTEC) ở cột A
    for (let r = 1; r <= sh.maxRow; r++) {
      const a = txt("A" + r);
      if (a && /^[A-ZĐ][A-ZĐ0-9 \-.]{2,}$/.test(a) && !/^K\d+$/.test(a) && !/^(STT|TUYẾN XE|KHỐI)/.test(a)) {
        if (!txt("B" + r)) { khoi = a; continue; }
      }
      // tìm ô STK: chuỗi/số 6-16 chữ số, ô kế bên phải là tên ngân hàng
      let stkC = 0;
      for (let c = 1; c < sh.maxCol; c++) {
        const v = txt(numToCol(c) + r).replace(/\s/g, "");
        if (/^\d{6,16}$/.test(v) && BANK_RE.test(txt(numToCol(c + 1) + r))) { stkC = c; break; }
      }
      if (!stkC) continue;
      let amt = 0;
      if (traCol) { const v = raw(traCol + r); amt = typeof v === "number" ? v : 0; }
      else for (let c = stkC - 1; c >= 1; c--) { const v = raw(numToCol(c) + r); if (typeof v === "number" && v >= 10000) { amt = v; break; } }
      const stk = txt(numToCol(stkC) + r).replace(/\s/g, ""); const bank = txt(numToCol(stkC + 1) + r); const chuTk = txt(numToCol(stkC + 2) + r);
      let note = ""; for (let c = 1; c <= sh.maxCol; c++) { const t = txt(numToCol(c) + r); if (t.toLowerCase().includes(MARK)) note = t; }
      // tài xế / tuyến để ghi chú
      let tuyen = ""; let taiXe = "";
      for (let rr = 1; rr <= Math.min(8, sh.maxRow); rr++) for (let c = 1; c <= sh.maxCol; c++) {
        const h = txt(numToCol(c) + rr).toLowerCase();
        if (!tuyen && (h === "tuyến" || h === "tuyến xe")) tuyen = txt(numToCol(c) + r);
        if (!taiXe && (h === "tài xế" || h === "lái xe" || h === "nhà xe")) taiXe = txt(numToCol(c) + r);
      }
      const nguon = traCol ? `Lương xe ${monthLabel}` : khoi ? `${sh.name} ${khoi}` : sh.name;
      out.push({ sheet: sh.name, si, row: r, stk, bank, chuTk, noiDung: ndCol ? txt(ndCol + r) : "", amt: Math.round(amt), quaGV: !!note, nguon, tuyen, taiXe: taiXe.replace(/\s+/g, " ") });
    }
  });
  return out;
}

const bankKey = (b) => String(b || "").toLowerCase().replace(/viettin/g, "vietin").replace(/bank/g, "").replace(/[^a-z]/g, "");
export function gopChuyenKhoan(list) {
  const g = {}; const order = [];
  list.forEach((x) => {
    const k = x.stk + "|" + bankKey(x.bank);
    if (!g[k]) { g[k] = { key: k, stk: x.stk, bank: x.bank, chuTk: x.chuTk, amt: 0, nguon: [], ct: [], noiDung: "" }; order.push(k); }
    if (x.noiDung && !g[k].noiDung) g[k].noiDung = x.noiDung;
    g[k].amt += x.amt;
    if (!g[k].nguon.includes(x.nguon)) g[k].nguon.push(x.nguon);
    g[k].ct.push(x);
  });
  return order.map((k) => g[k]).filter((x) => x.amt > 0);
}

// ===================== Giao diện =====================
const btn = { padding: "6px 12px", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#374151" };
const prevMonth = (mk) => { const [y, m] = mk.split("-").map(Number); const d = new Date(y, m - 2, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };

const money = (v) => (v ? Math.round(v).toLocaleString("vi-VN") : "");
const LXGV_HEAD = ["STT", "Tài xế", "Tuyến", "Cung đường", "Cước/ngày", "Tổng công", "Tổng tiền", "Vé cầu đường", "Thu nhập (Trả lái xe)", "Trừ bảo hiểm", "Thực nhận", "STK", "Ngân hàng", "Chủ TK", "Ghi chú"];
// Mặc định trừ BHXH: 5.310.000 × 32% − 200.000 = 1.499.200 (sửa được từng người).
const BHXH_LAI_XE = Math.round(5310000 * 0.32) - 200000;
const BHXH_LAI_XE_F = "5310000*32%-200000";
// Lái xe qua lương GV -> đợt Payroll "Lương T{MM}.{YYYY}", nhóm "Lái xe" (PCT): thay toàn bộ dòng nhóm Lái xe bằng danh sách hiện tại.
export async function dongBoPayrollLaiXe(schoolId, monthKey, list) {
  const tenDot = `Lương T${monthKey.slice(5, 7)}.${monthKey.slice(0, 4)}`;
  const batches = (await listPayrollBatches(schoolId)) || [];
  const b = (Array.isArray(batches) ? batches : Object.values(batches)).find((x) => String(x.name || "").trim().toLowerCase() === tenDot.toLowerCase());
  if (!b) return { ok: false, msg: `Chưa có đợt "${tenDot}" ở Payroll.` };
  const rows = (await getPayrollRows(schoolId, b.id)) || [];
  const cu = rows.filter((r) => r.group === "Lái xe");
  const giu = rows.filter((r) => r.group !== "Lái xe");
  const moi = list.filter((r) => r.thucNhan > 0).map((r) => {
    const old = cu.find((x) => keyTen(x.name) === keyTen(r.taiXe));
    return { ...(old || {}), source: "PCT", name: r.taiXe, amount: Math.round(r.thucNhan), account: r.stk, bank: r.nh, content: (old && old.content) || `PCT thanh toán lương ${tenDot.replace("Lương ", "")}`, note: (old && old.note) || "CH", group: "Lái xe" };
  });
  await setPayrollRows(schoolId, b.id, [...giu, ...moi]);
  return { ok: true, msg: `Đã cập nhật ${moi.length} lái xe vào Payroll "${tenDot}" (nhóm Lái xe).` };
}
export const tinhLaiXeGV = (rows, data) => rows.map((r) => {
  const k = keyTen(r.taiXe + "_" + r.sheet + "_" + r.tuyen);
  const nhap = data[k] && Number(data[k].bhxh);
  const bhxh = nhap || BHXH_LAI_XE;
  return { ...r, k, bhxh, macDinh: !nhap, thucNhan: r.thuNhap - bhxh };
});

const LaiXeGVView = ({ rows, monthLabel, monthKey, data, onChange, openCell, dirty, msg, onSync }) => {
  const list = tinhLaiXeGV(rows, data);
  const tong = (key) => list.reduce((a, r) => a + (r[key] || 0), 0);
  const th = { border: "1px solid #cbd5e1", padding: "6px 8px", background: "#f1f5f9", fontSize: 13, whiteSpace: "nowrap" };
  const td = { border: "1px solid #e2e8f0", padding: "5px 8px", fontSize: 13 };
  const exportX = () => {
    const R0 = 3; const last = R0 + list.length - 1;
    exportTableExcel({
      title: `Thu nhập lái xe đi qua tài khoản cùng lương GV — ${monthLabel}`, sheetName: "Lái xe qua lương GV", fileName: `laixe_qua_luong_gv_${monthKey}.xlsx`,
      header: LXGV_HEAD,
      rows: list.map((r, i) => [i + 1, r.taiXe, r.tuyen, r.cungDuong, r.cuoc, r.cong, r.tongTien, r.ve, r.thuNhap, r.macDinh ? { formula: BHXH_LAI_XE_F, result: r.bhxh } : r.bhxh, { formula: `I${R0 + i}-J${R0 + i}`, result: r.thucNhan }, r.stk, r.nh, r.chuTk, r.ghiChu]),
      moneyCols: [4, 6, 7, 8, 9, 10],
      totalRow: ["", "Tổng cộng", "", "", "", "", ...["G", "H", "I", "J", "K"].map((c, j) => ({ formula: `SUM(${c}${R0}:${c}${last})`, result: tong(["tongTien", "ve", "thuNhap", "bhxh", "thucNhan"][j]) })), "", "", "", ""],
    });
  };
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0 8px", flexWrap: "wrap" }}>
        <p style={{ margin: 0, color: "#6b7280", fontSize: 13, flex: 1, minWidth: 260 }}>
          Các lái xe có Ghi chú <i>"Đi qua tài khoản cùng lương GV, trừ tiền bảo hiểm"</i> trong bảng lương xe {monthLabel}. Thu nhập = cột <b>Trả lái xe</b> (tự tính lại khi sửa bảng). Trừ BHXH mặc định = 5.310.000 × 32% − 200.000 = <b>{money(BHXH_LAI_XE)}</b>/người; người nào khác thì sửa ô rồi bấm 💾 Lưu.
        </p>
        <button type="button" style={btn} onClick={exportX} disabled={!list.length}>⬇ Export Excel</button>
        <button type="button" style={{ ...btn, borderColor: "#0f766e", color: "#0f766e" }} onClick={() => onSync(list)} disabled={dirty} title={dirty ? "Lưu trước rồi mới đồng bộ" : ""}>↗ Cập nhật sang Payroll (PCT)</button>
      </div>
      {msg ? <p style={{ margin: "4px 0", fontSize: 13 }}>{msg}</p> : dirty ? <p style={{ margin: "4px 0", fontSize: 13, color: "#b45309" }}>Có thay đổi chưa lưu.</p> : null}
      {!list.length ? <p style={{ color: "#6b7280" }}>Không có lái xe nào có ghi chú này.</p> : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead><tr>{LXGV_HEAD.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {list.map((r, i) => (
                <tr key={r.k + i}>
                  <td style={{ ...td, textAlign: "center" }}>{i + 1}</td>
                  <td style={{ ...td, fontWeight: 600, whiteSpace: "nowrap" }}>{r.taiXe} <span title={`Xem ô ${r.traAddr} ở sheet ${r.sheet}`} onClick={() => openCell(r)} style={{ cursor: "pointer", color: "#2563eb", fontSize: 11 }}>↗</span></td>
                  <td style={{ ...td, textAlign: "center" }}>{r.tuyen}</td>
                  <td style={{ ...td, minWidth: 220 }}>{r.cungDuong}</td>
                  <td style={{ ...td, textAlign: "right" }}>{money(r.cuoc)}</td>
                  <td style={{ ...td, textAlign: "center" }}>{r.cong || ""}</td>
                  <td style={{ ...td, textAlign: "right" }}>{money(r.tongTien)}</td>
                  <td style={{ ...td, textAlign: "right" }}>{money(r.ve)}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{money(r.thuNhap)}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <input type="text" inputMode="numeric" defaultValue={r.bhxh ? r.bhxh.toLocaleString("vi-VN") : ""} key={r.k + "_" + r.bhxh}
                      onBlur={(e) => { const v = Number(String(e.target.value).replace(/[^\d]/g, "")) || 0; if (v !== r.bhxh) onChange(r.k, r.taiXe, v); }}
                      style={{ width: 100, textAlign: "right", padding: "3px 6px", border: "1px solid #cbd5e1", borderRadius: 4, color: "#dc2626", background: r.macDinh ? "#fff" : "#fef9c3" }} title={r.macDinh ? "Mặc định 5.310.000×32%−200.000" : "Đã sửa tay"} />
                  </td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, color: "#047857" }}>{money(r.thucNhan)}</td>
                  <td style={td}>{r.stk}</td>
                  <td style={td}>{r.nh}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{r.chuTk}</td>
                  <td style={{ ...td, color: "#6b7280", fontSize: 12 }}>{r.ghiChu}</td>
                </tr>
              ))}
              <tr style={{ background: "#ecfdf5", fontWeight: 700 }}>
                <td style={td} /><td style={td}>Tổng cộng ({list.length})</td><td style={td} /><td style={td} /><td style={td} /><td style={td} />
                <td style={{ ...td, textAlign: "right" }}>{money(tong("tongTien"))}</td>
                <td style={{ ...td, textAlign: "right" }}>{money(tong("ve"))}</td>
                <td style={{ ...td, textAlign: "right" }}>{money(tong("thuNhap"))}</td>
                <td style={{ ...td, textAlign: "right", color: "#dc2626" }}>{money(tong("bhxh"))}</td>
                <td style={{ ...td, textAlign: "right", color: "#047857" }}>{money(tong("thucNhan"))}</td>
                <td style={td} colSpan={4} />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Tiếng Việt không dấu, viết hoa chữ cái đầu mỗi từ (vd "lê Thị Lan" -> "Le Thi Lan").
const khongDau = (x) => String(x || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D");
const tenKhongDau = (x) => khongDau(x).trim().replace(/\s+/g, " ").toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());
const ChuyenKhoanXeView = ({ list, monthLabel, monthKey, ck = {}, chung = "", dirty = false, onCk, onChung }) => {
  const [ganLaiXe, setGanLaiXe] = useState(true);
  // Gồm CẢ lái xe "qua lương GV" (theo yêu cầu 23/10/2026) — lưu ý không chi trùng ở Payroll.
  const chinh = gopChuyenKhoan(list);
  const quaGV = list.filter((x) => x.quaGV && x.amt > 0);
  const tong = chinh.reduce((a, r) => a + r.amt, 0);
  const tongTheoNguon = {}; list.forEach((x) => { tongTheoNguon[x.nguon] = (tongTheoNguon[x.nguon] || 0) + x.amt; });
  const macDinhND = `TT tiền thuê xe đưa đón HSSV tháng ${Number(monthKey.slice(5, 7))}`;
  const noiDung = (r) => (ck[r.key] !== undefined ? String(ck[r.key]) : r.noiDung ? r.noiDung.replace(/\s+/g, " ").trim() : (chung.trim() || macDinhND));
  const ghiChu = (r) => r.ct.map((x) => `${x.tuyen ? "T" + x.tuyen + " " : ""}${ganLaiXe && x.taiXe ? x.taiXe.replace(/\(.*$/, "").trim() + " " : ""}${money(x.amt)}${x.quaGV ? " (qua lương GV)" : ""}`).join("; ");
  const th = { border: "1px solid #cbd5e1", padding: "6px 8px", background: "#f1f5f9", fontSize: 13, whiteSpace: "nowrap" };
  const td = { border: "1px solid #e2e8f0", padding: "5px 8px", fontSize: 13 };
  const esc = (x) => String(x ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const printList = () => {
    const body = chinh.map((r, i) => `<tr><td class="c">${i + 1}</td><td class="l b">${esc(tenKhongDau(r.chuTk))}</td><td class="b">${money(r.amt)}</td><td class="c">${esc(r.stk)}</td><td class="c">${esc(r.bank)}</td><td class="l">${esc(noiDung(r))}</td><td class="l sm">${esc(ghiChu(r))}</td></tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Chuyển khoản lương xe ${esc(monthLabel)}</title>
<style>@page{size:A4 landscape;margin:10mm}body{font-family:"Times New Roman",serif;font-size:14px;color:#000}h2{text-align:center;margin:4px 0 10px}
table{border-collapse:collapse;width:100%}th,td{border:1px solid #000;padding:9px 7px;text-align:right;line-height:1.35}th{background:#eee;text-align:center}
td.l{text-align:left}td.c{text-align:center}.b{font-weight:bold}.sm{font-size:12px}tr{page-break-inside:avoid}thead{display:table-header-group}.tot td{background:#f3f3f3;font-weight:bold}
.sign2{width:45%;margin:22px 0 0 auto;text-align:center;font-size:15px;line-height:1.7;page-break-inside:avoid}</style></head><body>
<div style="font-weight:bold">TRƯỜNG TRUNG CẤP KINH TẾ - KỸ THUẬT PHAN CHU TRINH</div>
<h2>DANH SÁCH CHUYỂN KHOẢN — LƯƠNG XE ${esc(monthLabel.toUpperCase())}</h2>
<table><thead><tr><th>STT</th><th>Họ và tên</th><th>Số tiền</th><th>Số tài khoản</th><th>Ngân hàng</th><th>Nội dung</th><th>Ghi chú</th></tr></thead>
<tbody>${body}<tr class="tot"><td colspan="2" class="l">Tổng cộng (${chinh.length} tài khoản)</td><td>${money(tong)}</td><td colspan="4"></td></tr></tbody></table>
<div class="sign2"><i>Hà Nội, ngày ...... tháng ...... năm ${esc(monthKey.slice(0, 4))}</i><br><b>Chủ tài khoản/Người được ủy quyền</b><br><i>(Ký, ghi rõ họ tên)</i></div>
<script>window.onload=function(){setTimeout(function(){window.print()},200)}</script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { window.alert("Trình duyệt chặn cửa sổ in — cho phép popup rồi bấm lại."); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };
  const exportX = () => {
    const R0 = 3; const last = R0 + chinh.length - 1;
    exportTableExcel({
      title: `Danh sách chuyển khoản lương xe — ${monthLabel}`, sheetName: "Chuyển khoản", fileName: `chuyen_khoan_luong_xe_${monthKey}.xlsx`,
      header: ["STT", "Họ và tên", "Số tiền", "Số tài khoản", "Ngân hàng", "Nội dung", "Ghi chú"],
      rows: chinh.map((r, i) => [i + 1, tenKhongDau(r.chuTk), r.amt, r.stk, r.bank, noiDung(r), ghiChu(r)]),
      moneyCols: [2],
      totalRow: ["", `Tổng cộng (${chinh.length} tài khoản)`, { formula: `SUM(C${R0}:C${last})`, result: tong }, "", "", "", ""],
    });
  };
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0 8px", flexWrap: "wrap" }}>
        <p style={{ margin: 0, color: "#6b7280", fontSize: 13, flex: 1, minWidth: 260 }}>
          Lấy từ mọi sheet: mỗi dòng có Số TK + Ngân hàng là 1 khoản; <b>gộp theo số tài khoản</b> (1 người nhận nhiều tuyến = 1 lần chuyển). Tự cập nhật khi sửa bảng.
          {" "}Theo nguồn: {Object.entries(tongTheoNguon).map(([k, v]) => `${k}: ${money(v)}`).join(" · ")}.
        </p>
        <label style={{ fontSize: 12 }}><input type="checkbox" checked={ganLaiXe} onChange={(e) => setGanLaiXe(e.target.checked)} /> Ghi chú kèm tên lái xe</label>
        <button type="button" style={btn} onClick={printList} disabled={!chinh.length}>🖨 In</button>
        <button type="button" style={btn} onClick={exportX} disabled={!chinh.length}>⬇ Export Excel</button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 8px", flexWrap: "wrap" }}>
        <b style={{ fontSize: 13 }}>Nội dung chung cho mọi dòng:</b>
        <input value={chung} placeholder={macDinhND} onChange={(e) => onChung && onChung(e.target.value)}
          style={{ flex: 1, minWidth: 280, padding: "5px 8px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 13 }} />
        <span style={{ fontSize: 12, color: dirty ? "#b45309" : "#6b7280" }}>{dirty ? "Có thay đổi chưa lưu — bấm 💾 Lưu ở trên" : "Sửa ô nội dung từng dòng nếu cần riêng"}</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead><tr>{["STT", "Họ và tên (chủ TK)", "Số tiền", "Số tài khoản", "Ngân hàng", "Nội dung", "Ghi chú (tuyến — số tiền)"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {chinh.map((r, i) => (
              <tr key={r.stk + i}>
                <td style={{ ...td, textAlign: "center" }}>{i + 1}</td>
                <td style={{ ...td, fontWeight: 600, whiteSpace: "nowrap" }}>{tenKhongDau(r.chuTk)}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{money(r.amt)}</td>
                <td style={{ ...td, fontFamily: "monospace" }}>{r.stk}</td>
                <td style={td}>{r.bank}</td>
                <td style={{ ...td, fontSize: 12, minWidth: 260 }}>
                  <input value={noiDung(r)} onChange={(e) => onCk && onCk(r.key, e.target.value)} title="Sửa trực tiếp nội dung chuyển khoản, rồi bấm 💾 Lưu"
                    style={{ width: "100%", boxSizing: "border-box", padding: "3px 6px", border: `1px solid ${ck[r.key] !== undefined ? "#f59e0b" : "#e2e8f0"}`, borderRadius: 4, fontSize: 12, background: ck[r.key] !== undefined ? "#fffbeb" : "#fff" }} />
                  {ck[r.key] !== undefined ? <button type="button" onClick={() => onCk && onCk(r.key, null)} title="Bỏ nội dung đã sửa, dùng lại mặc định" style={{ ...btn, padding: "1px 6px", marginTop: 3, fontSize: 11 }}>↺ Đặt lại</button> : null}
                </td>
                <td style={{ ...td, fontSize: 12, color: "#4b5563" }}>{ghiChu(r)}</td>
              </tr>
            ))}
            <tr style={{ background: "#ecfdf5", fontWeight: 700 }}>
              <td style={td} /><td style={td}>Tổng cộng ({chinh.length} tài khoản)</td>
              <td style={{ ...td, textAlign: "right" }}>{money(tong)}</td><td style={td} colSpan={4} />
            </tr>
          </tbody>
        </table>
      </div>
      {quaGV.length ? (
        <p style={{ fontSize: 13, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "6px 10px", marginTop: 10 }}>
          ⚠️ Danh sách trên ĐÃ GỒM lái xe đi qua tài khoản cùng lương GV: {quaGV.map((x) => `${x.taiXe} ${money(x.amt)}`).join("; ")} — tổng {money(quaGV.reduce((a, x) => a + x.amt, 0))}. Các lái xe này cũng có ở Payroll (nhóm Lái xe) — chỉ chi 1 nơi, tránh chuyển trùng.
        </p>
      ) : null}
    </div>
  );
};

const LuongXeTab = ({ schoolId, monthKey, monthLabel }) => {
  const [wb, setWb] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState("");
  const [si, setSi] = useState(0);
  const [sel, setSel] = useState(null);
  const [edit, setEdit] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const fileRef = useRef(null);
  const barRef = useRef(null);

  useEffect(() => {
    if (!schoolId) return;
    setLoaded(false); setErr(""); setWb(null); setDirty(false); setSel(null);
    getLuongXe(schoolId, monthKey).then((r) => { setWb(r && r.data ? { ...r.data, sheets: (r.data.sheets || []).map((s) => ({ ...s, cells: s.cells || {}, merges: s.merges || [], widths: s.widths || {}, hidden: s.hidden || [] })) } : null); setLoaded(true); })
      .catch((e) => { setErr(e.message || "Không tải được."); setLoaded(true); });
  }, [schoolId, monthKey]);
  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h); return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const engine = useMemo(() => (wb ? makeEngine(wb.sheets) : null), [wb]);
  const sh = wb && (si >= 0 ? wb.sheets[si] : wb.sheets[0]);
  const laiXe = useMemo(() => (wb && engine ? laiXeQuaLuongGV(wb, engine) : []), [wb, engine]);
  const ckList = useMemo(() => (wb && engine ? chuyenKhoanXe(wb, engine, monthLabel) : []), [wb, engine, monthLabel]);

  const mergeInfo = useMemo(() => {
    const top = {}; const covered = new Set();
    ((sh && sh.merges) || []).forEach((m) => {
      const [a, b] = m.split(":").map(splitAddr); if (!a || !b) return;
      top[numToCol(a.c) + a.r] = { rs: b.r - a.r + 1, cs: b.c - a.c + 1 };
      for (let r = a.r; r <= b.r; r++) for (let c = a.c; c <= b.c; c++) if (r !== a.r || c !== a.c) covered.add(numToCol(c) + r);
    });
    return { top, covered };
  }, [sh]);

  if (!loaded) return <p>Đang tải...</p>;

  const pickCell2 = (i, addr) => { const c = wb.sheets[i] && wb.sheets[i].cells[addr]; setSel(addr); setEdit(c ? (c.f ? "=" + c.f : c.v === undefined ? "" : String(c.v)) : ""); };
  const pickCell = (addr) => {
    setSel(addr);
    const c = sh.cells[addr];
    setEdit(c ? (c.f ? "=" + c.f : c.v === undefined ? "" : String(c.v)) : "");
  };
  const commit = (addr, text, move) => {
    if (!addr) return;
    const cur = sh.cells[addr] || {};
    const t = String(text);
    const o = { ...cur }; delete o.f; delete o.v;
    if (t.startsWith("=") && t.length > 1) o.f = t.slice(1);
    else if (t.trim() !== "") {
      const n = Number(t.replace(/\./g, "").replace(",", "."));
      const looksNum = /^-?[\d.]+(,\d+)?$/.test(t.trim()) && !/^0\d/.test(t.trim());
      o.v = looksNum && !isNaN(n) && cur.z !== "@" ? n : t;
    }
    const same = (cur.f || "") === (o.f || "") && cur.v === o.v;
    if (!same) {
      const cells = { ...sh.cells };
      if (o.v === undefined && !o.f && !o.bg && !o.b) delete cells[addr]; else cells[addr] = o;
      const a = splitAddr(addr);
      const sheets = wb.sheets.map((s, i) => (i === si ? { ...s, cells, maxRow: Math.max(s.maxRow, a.r), maxCol: Math.max(s.maxCol, a.c) } : s));
      setWb({ ...wb, sheets }); setDirty(true); setMsg("");
    }
    if (move) { const a = splitAddr(addr); const nx = numToCol(a.c) + (a.r + 1); setSel(nx); const c = sh.cells[nx]; setEdit(c ? (c.f ? "=" + c.f : c.v === undefined ? "" : String(c.v)) : ""); }
  };
  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await saveLuongXe(schoolId, monthKey, { sheets: wb.sheets, laiXeGV: wb.laiXeGV || {}, fileName: wb.fileName || "", ckNoiDung: wb.ckNoiDung || {}, ckNoiDungChung: wb.ckNoiDungChung || "" }); setDirty(false);
      let m2 = "";
      try { const r = await dongBoPayrollLaiXe(schoolId, monthKey, tinhLaiXeGV(laiXe, wb.laiXeGV || {})); m2 = " " + r.msg; } catch (e2) { m2 = " (Chưa cập nhật được Payroll: " + e2.message + ")"; }
      setMsg("✅ Đã lưu." + m2);
    }
    catch (e) { setMsg("❌ " + (e.message || "Lỗi lưu")); }
    setSaving(false);
  };
  const onImport = async (e) => {
    const f = e.target.files && e.target.files[0]; e.target.value = "";
    if (!f) return;
    if (wb && !window.confirm(`Nhập file "${f.name}" sẽ THAY toàn bộ bảng Lương xe ${monthLabel}. Tiếp tục?`)) return;
    try { const data = await readWorkbookFile(f); setWb({ ...data, laiXeGV: (wb && wb.laiXeGV) || {} }); setSi(0); setSel(null); setDirty(true); setMsg(`Đã đọc ${data.sheets.length} sheet — bấm 💾 Lưu để ghi.`); }
    catch (er) { setMsg("❌ Không đọc được file: " + er.message); }
  };
  const copyPrev = async () => {
    try {
      const r = await getLuongXe(schoolId, prevMonth(monthKey));
      if (!r || !r.data) { setMsg("Tháng trước chưa có bảng Lương xe."); return; }
      setWb({ ...r.data, sheets: (r.data.sheets || []).map((s) => ({ ...s, cells: s.cells || {}, merges: s.merges || [], widths: s.widths || {}, hidden: s.hidden || [] })) }); setSi(0); setDirty(true);
      setMsg("Đã chép bảng tháng trước — sửa tháng/năm, chấm ngày rồi bấm 💾 Lưu.");
    } catch (e) { setMsg("❌ " + e.message); }
  };
  const rowOp = (kind) => {
    if (!sel) { setMsg("Chọn 1 ô trước."); return; }
    const r = splitAddr(sel).r;
    if (kind === "del" && !window.confirm(`Xoá dòng ${r}?`)) return;
    const nw = kind === "above" ? insertRow(wb, si, r, null) : kind === "below" ? insertRow(wb, si, r + 1, r) : deleteRow(wb, si, r);
    setWb(nw); setDirty(true); setMsg("");
  };
  const addSheet = () => {
    const name = window.prompt("Tên sheet mới:"); if (!name) return;
    setWb({ ...(wb || { fileName: "" }), sheets: [...((wb && wb.sheets) || []), { name, maxRow: 30, maxCol: 12, cells: {}, merges: [], widths: {}, hidden: [] }] });
    setSi(wb ? wb.sheets.length : 0); setDirty(true);
  };
  const renameSheet = () => {
    if (si < 0) return;
    const name = window.prompt("Đổi tên sheet:", sh.name); if (!name || name === sh.name) return;
    setWb({ ...wb, sheets: wb.sheets.map((s, i) => (i === si ? { ...s, name } : s)) }); setDirty(true);
  };
  const delSheet = () => {
    if (si < 0) return;
    if (!window.confirm(`Xoá sheet "${sh.name}"?`)) return;
    const sheets = wb.sheets.filter((_, i) => i !== si); setWb(sheets.length ? { ...wb, sheets } : null); setSi(0); setDirty(true);
  };

  const toolbar = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
      <b style={{ fontSize: 15 }}>🚌 Lương xe {monthLabel}</b>
      {wb && wb.fileName ? <span style={{ color: "#6b7280", fontSize: 12 }}>({wb.fileName})</span> : null}
      <span style={{ flex: 1 }} />
      <input ref={fileRef} type="file" accept=".xlsx" style={{ display: "none" }} onChange={onImport} />
      <button type="button" style={btn} onClick={() => fileRef.current && fileRef.current.click()}>📥 Nhập file Excel</button>
      <button type="button" style={btn} onClick={copyPrev}>📋 Chép từ tháng trước</button>
      {wb ? <button type="button" style={btn} onClick={() => exportWorkbook(wb, `LUONG_XE_${monthKey}.xlsx`)}>⬇ Export Excel</button> : null}
      {wb ? <button type="button" onClick={save} disabled={!dirty || saving} style={{ ...btn, background: dirty ? "#0f766e" : "#e5e7eb", color: dirty ? "#fff" : "#6b7280", border: "none" }}>{saving ? "Đang lưu..." : "💾 Lưu"}</button> : null}
    </div>
  );

  if (!wb || !sh) {
    return (
      <div>
        {toolbar}
        {err ? <p style={{ color: "#dc2626" }}>{err}</p> : null}
        <p style={{ color: "#6b7280" }}>Chưa có bảng Lương xe tháng này — bấm <b>📥 Nhập file Excel</b> (mỗi sheet thành 1 tab) hoặc <b>📋 Chép từ tháng trước</b>. {msg}</p>
      </div>
    );
  }

  const hiddenSet = new Set(showHidden ? [] : sh.hidden || []);
  const cols = []; for (let c = 1; c <= Math.max(sh.maxCol, 1); c++) { const L = numToCol(c); if (!hiddenSet.has(L)) cols.push(L); }
  const rowsN = Math.max(sh.maxRow, 1);
  const wpx = (L) => { const w = sh.widths[L]; return Math.max(28, Math.round((w || 9) * 7 + 6)); };
  const selA = sel && splitAddr(sel);

  // In 1 sheet: dựng bảng HTML từ giá trị đã tính (bỏ cột ẩn, bỏ dòng/cột trống cuối), gộp ô, định dạng, tự co theo bề rộng khổ giấy ngang.
  const printSheet = () => {
    const esc = (x) => String(x ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    let lastR = 0; let lastC = 0;
    Object.entries(sh.cells).forEach(([a, c]) => {
      const v = c.f ? engine.value(si, a) : c.v;
      if (v === null || v === undefined || v === "") return;
      const p = splitAddr(a); if (!p) return;
      lastR = Math.max(lastR, p.r); lastC = Math.max(lastC, p.c);
    });
    (sh.merges || []).forEach((m) => { const [a, b] = m.split(":").map(splitAddr); if (a && b && a.r <= lastR) lastC = Math.max(lastC, Math.min(b.c, sh.maxCol)); });
    if (!lastR) { window.alert("Sheet trống, không có gì để in."); return; }
    const pcols = []; for (let c = 1; c <= lastC; c++) { const L = numToCol(c); if (!hiddenSet.has(L)) pcols.push(L); }
    const totalW = pcols.reduce((a, L) => a + wpx(L), 0);
    const zoom = Math.min(1, 1040 / Math.max(totalW, 1));
    let body = "";
    for (let r = 1; r <= lastR; r++) {
      let tr = "";
      pcols.forEach((L) => {
        const addr = L + r;
        if (mergeInfo.covered.has(addr)) return;
        const m = mergeInfo.top[addr];
        let cs = m ? m.cs : 1;
        if (m) { const c0 = colToNum(L); cs = 0; for (let c = c0; c < c0 + m.cs; c++) if (!hiddenSet.has(numToCol(c))) cs++; cs = Math.max(cs, 1); }
        const c = sh.cells[addr];
        const v = c ? (c.f ? engine.value(si, addr) : c.v) : null;
        const text = c ? formatValue(v, c.z) : "";
        const isNum = typeof v === "number";
        const st = `text-align:${(c && c.h) || (isNum ? "right" : "left")};${c && c.b ? "font-weight:bold;" : ""}${c && c.i ? "font-style:italic;" : ""}${c && c.bg ? `background:${c.bg};` : ""}${c && c.fc ? `color:${c.fc};` : ""}${c && c.w ? "white-space:normal;" : "white-space:nowrap;"}`;
        tr += `<td${m && m.rs > 1 ? ` rowspan="${m.rs}"` : ""}${cs > 1 ? ` colspan="${cs}"` : ""} style="${esc(st)}">${esc(text)}</td>`;
      });
      body += `<tr>${tr}</tr>`;
    }
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Lương xe ${esc(monthLabel)} — ${esc(sh.name)}</title>
<style>@page{size:A4 landscape;margin:8mm}body{font-family:"Times New Roman",serif;color:#000;margin:0}h3{text-align:center;margin:2px 0 8px;font-size:15px}
table{border-collapse:collapse;table-layout:fixed;zoom:${zoom.toFixed(3)};font-size:11px}td{border:1px solid #000;padding:2px 3px;overflow:hidden;line-height:1.25}tr{page-break-inside:avoid}</style></head><body>
<h3>LƯƠNG XE ${esc(monthLabel.toUpperCase())} — ${esc(sh.name)}</h3>
<table><colgroup>${pcols.map((L) => `<col style="width:${wpx(L)}px">`).join("")}</colgroup><tbody>${body}</tbody></table>
<script>window.onload=function(){setTimeout(function(){window.print()},250)}</script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { window.alert("Trình duyệt chặn cửa sổ in — cho phép popup rồi bấm lại."); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  return (
    <div>
      {toolbar}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6, alignItems: "center" }}>
        {wb.sheets.map((s, i) => (
          <button key={i} type="button" onClick={() => { setSi(i); setSel(null); setEdit(""); }}
            style={{ padding: "6px 12px", borderRadius: "6px 6px 0 0", border: "1px solid #cbd5e1", borderBottom: i === si ? "2px solid #0f766e" : "1px solid #cbd5e1", background: i === si ? "#ecfdf5" : "#f8fafc", fontWeight: i === si ? 700 : 500, cursor: "pointer", fontSize: 13 }}>
            {s.name}
          </button>
        ))}
        <button type="button" onClick={() => { setSi(-1); setSel(null); setEdit(""); }}
          style={{ padding: "6px 12px", borderRadius: "6px 6px 0 0", border: "1px solid #f59e0b", borderBottom: si === -1 ? "2px solid #b45309" : "1px solid #f59e0b", background: si === -1 ? "#fef3c7" : "#fffbeb", fontWeight: si === -1 ? 700 : 500, cursor: "pointer", fontSize: 13 }}>
          💼 Lái xe qua lương GV ({laiXe.length})
        </button>
        <button type="button" onClick={() => { setSi(-2); setSel(null); setEdit(""); }}
          style={{ padding: "6px 12px", borderRadius: "6px 6px 0 0", border: "1px solid #3b82f6", borderBottom: si === -2 ? "2px solid #1d4ed8" : "1px solid #3b82f6", background: si === -2 ? "#dbeafe" : "#eff6ff", fontWeight: si === -2 ? 700 : 500, cursor: "pointer", fontSize: 13 }}>
          🏦 Chuyển khoản
        </button>
        <button type="button" style={{ ...btn, padding: "4px 8px" }} onClick={addSheet} title="Thêm sheet">＋</button>
        <button type="button" style={{ ...btn, padding: "4px 8px" }} onClick={renameSheet} title="Đổi tên sheet">✏️</button>
        <button type="button" style={{ ...btn, padding: "4px 8px", color: "#dc2626" }} onClick={delSheet} title="Xoá sheet">🗑</button>
        <span style={{ flex: 1 }} />
        {(sh.hidden || []).length ? <label style={{ fontSize: 12, color: "#374151" }}><input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} /> Hiện cột ẩn ({sh.hidden.join(", ")})</label> : null}
      </div>
      {si === -2 ? (
        <ChuyenKhoanXeView list={ckList} monthLabel={monthLabel} monthKey={monthKey}
          ck={wb.ckNoiDung || {}} chung={wb.ckNoiDungChung || ""} dirty={dirty}
          onCk={(k, t) => { const n = { ...(wb.ckNoiDung || {}) }; if (t === null) delete n[k]; else n[k] = t; setWb({ ...wb, ckNoiDung: n }); setDirty(true); }}
          onChung={(t) => { setWb({ ...wb, ckNoiDungChung: t }); setDirty(true); }} />
      ) : si === -1 ? (
        <LaiXeGVView rows={laiXe} monthLabel={monthLabel} monthKey={monthKey} data={wb.laiXeGV || {}}
          onChange={(k, hoten, bhxh) => { setWb({ ...wb, laiXeGV: { ...(wb.laiXeGV || {}), [k]: { hoten, bhxh } } }); setDirty(true); setMsg(""); }}
          onSync={async (list) => { try { const r = await dongBoPayrollLaiXe(schoolId, monthKey, list); setMsg((r.ok ? "✅ " : "⚠️ ") + r.msg); } catch (e) { setMsg("❌ " + e.message); } }}
          openCell={(r) => { const i = wb.sheets.findIndex((x) => x.name === r.sheet); setSi(i); setTimeout(() => pickCell2(i, r.traAddr), 0); }} dirty={dirty} msg={msg} />
      ) : (<>
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
        <span style={{ minWidth: 54, fontWeight: 700, fontFamily: "monospace", background: "#f3f4f6", padding: "5px 8px", borderRadius: 4, textAlign: "center" }}>{sel || "—"}</span>
        <input ref={barRef} value={edit} disabled={!sel} onChange={(e) => setEdit(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(sel, edit, true); } if (e.key === "Escape") pickCell(sel); }}
          onBlur={() => sel && commit(sel, edit, false)}
          placeholder="Chọn ô rồi gõ giá trị hoặc công thức (bắt đầu bằng =), Enter để ghi"
          style={{ flex: 1, minWidth: 260, padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 4, fontFamily: "monospace", fontSize: 13 }} />
        <button type="button" style={btn} onClick={() => rowOp("above")} title="Chèn dòng trống phía trên ô đang chọn">↥ Chèn dòng trên</button>
        <button type="button" style={btn} onClick={() => rowOp("below")} title="Chèn dòng phía dưới, chép công thức của dòng đang chọn">↧ Chèn dòng dưới (chép công thức)</button>
        <button type="button" style={{ ...btn, color: "#dc2626" }} onClick={() => rowOp("del")}>✖ Xoá dòng</button>
        <button type="button" style={btn} onClick={printSheet} title="In sheet đang xem (khổ A4 ngang, tự co vừa trang)">🖨 In sheet này</button>
      </div>
      {msg ? <p style={{ margin: "4px 0", fontSize: 13 }}>{msg}{dirty ? " — chưa lưu" : ""}</p> : dirty ? <p style={{ margin: "4px 0", fontSize: 13, color: "#b45309" }}>Có thay đổi chưa lưu.</p> : null}
      <div style={{ overflow: "auto", maxHeight: "72vh", border: "1px solid #cbd5e1" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 12.5, tableLayout: "fixed", fontFamily: "'Times New Roman', serif" }}>
          <colgroup><col style={{ width: 36 }} />{cols.map((L) => <col key={L} style={{ width: wpx(L) }} />)}</colgroup>
          <thead>
            <tr>
              <th style={{ position: "sticky", top: 0, left: 0, zIndex: 3, background: "#e5e7eb", border: "1px solid #cbd5e1" }} />
              {cols.map((L) => <th key={L} style={{ position: "sticky", top: 0, zIndex: 2, background: selA && numToCol(selA.c) === L ? "#a7f3d0" : "#e5e7eb", border: "1px solid #cbd5e1", fontWeight: 500, fontSize: 11, color: "#4b5563" }}>{L}</th>)}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rowsN }, (_, ri) => {
              const r = ri + 1;
              return (
                <tr key={r}>
                  <td style={{ position: "sticky", left: 0, zIndex: 1, background: selA && selA.r === r ? "#a7f3d0" : "#f3f4f6", border: "1px solid #cbd5e1", textAlign: "center", fontSize: 11, color: "#4b5563" }}>{r}</td>
                  {cols.map((L) => {
                    const addr = L + r;
                    if (mergeInfo.covered.has(addr)) return null;
                    const m = mergeInfo.top[addr];
                    let cs = m ? m.cs : 1;
                    if (m && !showHidden) { const c0 = colToNum(L); cs = 0; for (let c = c0; c < c0 + m.cs; c++) if (!hiddenSet.has(numToCol(c))) cs++; cs = Math.max(cs, 1); }
                    const c = sh.cells[addr];
                    const v = c ? (c.f ? engine.value(si, addr) : c.v) : null;
                    const text = c ? formatValue(v, c.z) : "";
                    const isNum = typeof v === "number";
                    const isSel = sel === addr;
                    return (
                      <td key={addr} rowSpan={m ? m.rs : undefined} colSpan={cs > 1 ? cs : undefined}
                        onClick={() => pickCell(addr)}
                        onDoubleClick={() => { pickCell(addr); setTimeout(() => barRef.current && barRef.current.focus(), 0); }}
                        title={c && c.f ? "=" + c.f : undefined}
                        style={{
                          border: "1px solid #e2e8f0", padding: "2px 4px", cursor: "cell",
                          whiteSpace: c && c.w ? "normal" : "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          textAlign: (c && c.h) || (isNum ? "right" : "left"),
                          fontWeight: c && c.b ? 700 : 400, fontStyle: c && c.i ? "italic" : "normal",
                          color: v && isErr(v) ? "#dc2626" : c && c.fc ? c.fc : "#111827",
                          background: isSel ? "#bfdbfe" : c && c.bg ? c.bg : c && c.f ? "#fafffd" : "#fff",
                          outline: isSel ? "2px solid #2563eb" : "none", outlineOffset: -2,
                        }}>
                        {text}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
        Bấm 1 ô để xem/sửa ở thanh công thức (ô có công thức nền xanh nhạt, rê chuột để xem công thức). Gõ <code>=</code> để nhập công thức — hỗ trợ + − × ÷, SUM, SUBTOTAL, IF, ROUND, DATE, TEXT, SUMIF... và tham chiếu sang sheet khác (<code>'Lương tháng 9'!AW55</code>). Sửa xong bấm <b>💾 Lưu</b>.
      </p>
      </>)}
    </div>
  );
};

export default LuongXeTab;
